import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

const FEATURED_LISTINGS_LIMIT = 8;
const IMAGE_DELIVERY_BASE = "https://imagedelivery.net/WefzwPpyYAgz4nGrybaw0Q";
const API_BASE_URL = "https://bluewaterdubai.websiteseoonline.com";
 

function buildApiUrl(pathname) {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || API_BASE_URL).replace(/\/$/, "");
  return `${baseUrl}${pathname}`;
}

function normalizeImagePath(image) {
  if (!image) {
    return "/assets/img/popular/popular-1-1.jpg";
  }

  if (/^https?:\/\//i.test(image) || image.startsWith("/")) {
    return image;
  }

  if (image.startsWith("assets/")) {
    return `/${image}`;
  }

  return `${IMAGE_DELIVERY_BASE}/${image.replace(/^\/+/, "")}`;
}

function formatPrice(price) {
  const numericPrice = Number(String(price || "").replace(/[^\d.]/g, ""));

  if (!numericPrice) {
    return "Price on request";
  }

  return `AED ${new Intl.NumberFormat("en-AE", {
    maximumFractionDigits: 0,
  }).format(numericPrice)}`;
}

function formatLocation(property) {
  return [property.community, property.city].filter(Boolean).join(" - ") || "Dubai - UAE";
}

function offeringTypeBadge(property) {
  const rawType = String(property.offering_type || property.offeringType || "").trim();
  const normalizedType = rawType.toLowerCase();

  if (!rawType) {
    return null;
  }

  if (normalizedType.includes("rent") || ["rr", "cr", "r"].includes(normalizedType)) {
    return { label: "Rent", tone: "rent" };
  }

  if (normalizedType.includes("sale") || normalizedType.includes("buy") || ["rs", "cs", "s"].includes(normalizedType)) {
    return { label: "Sale", tone: "sale" };
  }

  return { label: rawType, tone: "default" };
}

function propertyUrl(property) {
  const referenceNumber = property.reference_number || property.id;
  return referenceNumber ? `/property-details/${encodeURIComponent(referenceNumber)}` : "/property-details";
}

function absolutePropertyUrl(property) {
  const detailUrl = propertyUrl(property);

  if (/^https?:\/\//i.test(detailUrl) || typeof window === "undefined") {
    return detailUrl;
  }

  return `${window.location.origin}${detailUrl}`;
}

function whatsappMessage(property) {
  const propertyTitle = property.title || "this property";
  const referenceNumber = property.reference_number || property.id;
  const messageParts = [
    `Hello Blue Water Real Estate, I am interested in ${propertyTitle}.`,
    referenceNumber ? `Reference: ${referenceNumber}.` : "",
    `Property details: ${absolutePropertyUrl(property)}`,
    "Please share more information.",
  ];

  return messageParts.filter(Boolean).join(" ");
}

function whatsappUrl(property) {
  const rawPhone = property.agent_whatsapp || property.agent_phone || "";
  const phone = String(rawPhone).replace(/[^\d+]/g, "");
  const message = encodeURIComponent(whatsappMessage(property));

  if (!phone) {
    return `https://api.whatsapp.com/send?phone=971507455838&text=${message}`;
  }

  return `https://api.whatsapp.com/send?phone=${encodeURIComponent(phone)}&text=${message}`;
}

function openImageGallery(event, images) {
  event.preventDefault();

  const galleryItems = images.filter(Boolean).map((src) => ({ src }));
  const $ = window.jQuery || window.$;

  if (galleryItems.length && $?.magnificPopup?.open) {
    $.magnificPopup.open(
      {
        items: galleryItems,
        gallery: {
          enabled: true,
        },
        type: "image",
        mainClass: "mfp-zoom-in",
        removalDelay: 260,
      },
      0
    );
    return;
  }

  if (galleryItems[0]?.src) {
    window.location.href = galleryItems[0].src;
  }
}

function uniqueImages(images) {
  return [...new Set(images.filter(Boolean))];
}

function ListingCard({ property }) {
  const images = useMemo(() => {
    const photos = Array.isArray(property.photos) ? property.photos : [];
    return photos.length ? uniqueImages(photos.map((photo) => normalizeImagePath(photo.image))) : [normalizeImagePath()];
  }, [property.photos]);

  const detailUrl = propertyUrl(property);
  const firstImage = images[0];
  const badge = offeringTypeBadge(property);

  return (
    <div className="col-xxl-3 col-xl-3 col-lg-6 col-md-6 filter-item">
      <div className="popular-list-1 property-listing-card">
        <div className="thumb-wrapper">
          <a className="popular-popup-image" href={firstImage} onClick={(event) => openImageGallery(event, images)}>
            <img src={firstImage} alt={property.title || "Property"} />
          </a>
          {badge ? <span className={`property-offering-badge ${badge.tone}`}>{badge.label}</span> : null}
          <div className="actions-style-2-wrapper">
            <div className="actions style-2">
              <Link to={detailUrl} className="icon-btn">
                <i className="fa-solid fa-link" />
              </Link>
              <a href={firstImage} className="icon-btn popular-popup-image" onClick={(event) => openImageGallery(event, images)}>
                <i className="fa-solid fa-camera" />
              </a>
            </div>
          </div>
        </div>
        <div className="property-content">
          <div className="media-body">
            <h3 className="box-title">
              <Link to={detailUrl}>{property.title || "Featured Property"}</Link>
            </h3>
            <div className="box-text">
              <div className="icon">
                <img src="/assets/img/icon/2.webp" alt="location" />
              </div>
              {formatLocation(property)}
            </div>
          </div>
          <ul className="property-featured">
            <li>
              <div className="icon">
                <img src="/assets/img/icon/bed.svg" alt="bed" />
              </div>
              Bed {property.bedrooms || "-"}
            </li>
            <li>
              <div className="icon">
                <img src="/assets/img/icon/bath.svg" alt="bath" />
              </div>
              Bath {property.No_of_Bathroom || "-"}
            </li>
            <li>
              <div className="icon">
                <img src="/assets/img/icon/sqft.svg" alt="area" />
              </div>
              {property.size || "-"} sqft
            </li>
          </ul>
          <div className="property-bottom">
            <h6 className="box-title">{formatPrice(property.price)}</h6>
            <a className="th-btn sm style3 pill" href={`tel:${property.agent_phone || ""}`}>
              <i className="fa-solid fa-phone" />
            </a>
            <a
              className="th-btn sm style3 pill"
              href={whatsappUrl(property)}
              target="_blank"
              rel="noreferrer"
              style={{ padding: "0" }}
            >
              <img src="/assets/img/icon/3.webp" width="38px" alt="WhatsApp" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedListings() {
  const [activeType, setActiveType] = useState("");
  const [properties, setProperties] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    async function loadListings() {
      setStatus("loading");
      setError("");

      try {
        const response = await fetch(buildApiUrl("/api/featuredlistings.ashx"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ typ: activeType }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Unable to load featured listings (${response.status})`);
        }

        const payload = await response.json();

        if (payload.result !== 1) {
          throw new Error(payload.message || "Unable to load featured listings");
        }

        setProperties(Array.isArray(payload.data) ? payload.data.slice(0, FEATURED_LISTINGS_LIMIT) : []);
        setStatus("ready");
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }

        setProperties([]);
        setError(err.message || "Unable to load featured listings");
        setStatus("error");
      }
    }

    loadListings();

    return () => controller.abort();
  }, [activeType]);

  return (
    <section className="popular-sec-1 space">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-7">
            <div className="title-area text-center mb-2">
              <h2 className="sec-title mb-4 fadeinup wow" data-wow-duration="1.3s" data-wow-delay="0.3s">
                Featured Listing Properties
              </h2>
             
            </div>
          </div>
        </div>
        {status === "loading" && (
          <div className="row gy-30 justify-content-center">
            <div className="col-12 text-center">Loading featured properties...</div>
          </div>
        )}
        {status === "error" && (
          <div className="row gy-30 justify-content-center">
            <div className="col-12 text-center">{error}</div>
          </div>
        )}
        {status === "ready" && properties.length === 0 && (
          <div className="row gy-30 justify-content-center">
            <div className="col-12 text-center">No featured properties found.</div>
          </div>
        )}
        {status === "ready" && properties.length > 0 && (
          <div className="row gy-30 justify-content-center filter-active">
            {properties.map((property) => (
              <ListingCard key={property.id || property.reference_number || property.title} property={property} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default FeaturedListings;
